// Stops the variance band flickering on a threshold: escalation applies at once, de-escalation
// only after holding for DE_ESCALATION_HOLD_MINUTES. Memory is per session (lost on reload).
import { type Variance, VARIANCE_ORDER } from "@/lib/tone"

export const DE_ESCALATION_HOLD_MINUTES = 3

export interface VarianceState {
  shown: Variance
  /** A better band seen recently, not yet adopted. */
  pending?: Variance
  /** Epoch ms. */
  pendingSince?: number
}

const severity = (v: Variance) => VARIANCE_ORDER.indexOf(v)

/** Returns null when the route has left the axis (cancelled), so the caller drops its state. */
export function stabiliseVariance(
  observed: Variance | null,
  previous: VarianceState | undefined,
  now: Date,
  holdMinutes: number = DE_ESCALATION_HOLD_MINUTES,
): VarianceState | null {
  if (observed === null) return null
  if (!previous) return { shown: observed }

  if (severity(observed) >= severity(previous.shown)) {
    return { shown: observed }
  }

  const startedAt =
    previous.pending === observed && previous.pendingSince !== undefined
      ? previous.pendingSince
      : now.getTime()

  if (now.getTime() - startedAt >= holdMinutes * 60_000) {
    return { shown: observed }
  }

  return { shown: previous.shown, pending: observed, pendingSince: startedAt }
}

/** Rebuilds the map so state for routes that have gone away is dropped. */
export function stabiliseAll(
  observations: Map<string, Variance | null>,
  previous: Map<string, VarianceState>,
  now: Date,
  holdMinutes: number = DE_ESCALATION_HOLD_MINUTES,
): Map<string, VarianceState> {
  const next = new Map<string, VarianceState>()
  for (const [id, observed] of observations) {
    const state = stabiliseVariance(observed, previous.get(id), now, holdMinutes)
    if (state) next.set(id, state)
  }
  return next
}
